/**
 * Routing health view (roadmap #54): lists the kiosks (QR codes) whose call
 * routing is broken — no default staff, a default that is not a real agent
 * (a client/owner account), or an agent without a registered device. Computed
 * client-side from the QR, staff (role 3) and organization lists; no backend
 * change is assumed.
 */

import { useMemo } from 'react';
import { useGetList, useTranslate } from 'react-admin';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { evaluateQrRouting, type RoutingStatus } from '@/features/qrcodes/routingHealth';

const STATUS_SEVERITY: Record<Exclude<RoutingStatus, 'ok'>, 'error' | 'warning'> = {
  default_not_agent: 'error',
  default_no_device: 'error',
  no_default: 'warning',
};

export const RoutingHealthView = () => {
  const translate = useTranslate();

  const { data: qrcodes, isLoading: qrLoading } = useGetList('qrcodes', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'qr_name', order: 'ASC' },
  });
  const { data: staff, isLoading: staffLoading } = useGetList('staff', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'full_name', order: 'ASC' },
  });
  const { data: organizations, isLoading: orgLoading } = useGetList('organizations', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'full_name', order: 'ASC' },
  });

  const loading = qrLoading || staffLoading || orgLoading;

  const { broken, total } = useMemo(() => {
    const staffById = new Map<number, any>();
    (staff ?? []).forEach((s: any) => staffById.set(Number(s.user_id), s));
    const orgById = new Map<number, any>();
    (organizations ?? []).forEach((o: any) => orgById.set(Number(o.user_id), o));

    const activeQrs = (qrcodes ?? []).filter((qr: any) => qr.status !== 'inactive');
    const rows = activeQrs
      .map((qr: any) => {
        const status = evaluateQrRouting(qr.default_staff_id, staffById);
        const org = orgById.get(Number(qr.organization_id));
        const def = qr.default_staff_id != null ? staffById.get(Number(qr.default_staff_id)) : null;
        return {
          id: qr.qrcode_id,
          qrName: qr.qr_name,
          orgName: org?.company_name || org?.full_name || '',
          defaultName: def?.full_name || (qr.default_staff_id ? `#${qr.default_staff_id}` : ''),
          status,
        };
      })
      .filter((r: any) => r.status !== 'ok');
    return { broken: rows, total: activeQrs.length };
  }, [qrcodes, staff, organizations]);

  if (loading) {
    return <CircularProgress size={28} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="info">
        {translate('pages.system.routing.intro')}
      </Alert>

      {broken.length === 0 ? (
        <Alert severity="success">{translate('pages.system.routing.all_ok')}</Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">
            {translate('pages.system.routing.summary', { broken: broken.length, total })}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate('pages.system.routing.kiosk')}</TableCell>
                <TableCell>{translate('pages.system.routing.organization')}</TableCell>
                <TableCell>{translate('pages.system.routing.default_staff')}</TableCell>
                <TableCell>{translate('pages.system.routing.problem')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {broken.map((row: any) => (
                <TableRow key={row.id}>
                  <TableCell>{row.qrName}</TableCell>
                  <TableCell>{row.orgName}</TableCell>
                  <TableCell>{row.defaultName || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={STATUS_SEVERITY[row.status as Exclude<RoutingStatus, 'ok'>]}
                      label={translate(`pages.system.routing.status.${row.status}`)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
};

export default RoutingHealthView;
